import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SPINNER_MOTION } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  buildCodeServerUrl,
  customerLabel,
  ENGINEER_VM_EXTEND_HOURS,
  EngineerVM,
  EngineerVMAction,
  engineerVMPhase,
  engineerVMPhaseLabel,
  engineerVMPhaseVariant,
  isTransitionalPhase,
  useCodeServerToken,
  useEngineerVMAction,
  useEngineerVMs,
} from '@/hooks/useEngineerVMs';
import { cn } from '@/lib/utils';
import { useAuth } from '@/state/auth';

interface EngineerVMDrawerProps {
  vm: EngineerVM | null;
  onClose: () => void;
}

export function EngineerVMDrawer({ vm, onClose }: EngineerVMDrawerProps) {
  const action = useEngineerVMAction();
  const codeServer = useCodeServerToken();
  const { me } = useAuth();
  // Re-read the live row so the drawer re-renders through transitions
  // (cache-deduped with the section's query).
  const { data: vms = [] } = useEngineerVMs(!!vm);
  const live = vm ? (vms.find(v => v.vm_id === vm.vm_id) ?? vm) : null;

  const phase = live ? engineerVMPhase(live) : 'provisioning';
  const transitional = isTransitionalPhase(phase);
  const busy = action.isPending || codeServer.isPending;
  const isRunning = phase === 'running';

  const openIDE = async () => {
    if (!live) return;
    try {
      const { token, gateway_url } = await codeServer.mutateAsync({
        vmId: live.vm_id,
        tenant: live.tenant ?? '',
      });
      window.open(
        buildCodeServerUrl(gateway_url, token),
        '_blank',
        'noopener,noreferrer'
      );
    } catch (error) {
      // Surface the backend refusal verbatim — the API wrapper puts the
      // response text on `.name`.
      toast.error(
        error instanceof Error && error.name
          ? error.name
          : 'Failed to open code-server.'
      );
    }
  };

  const run = async (a: EngineerVMAction) => {
    if (!live) return;
    try {
      await action.mutateAsync({
        vmId: live.vm_id,
        action: a,
        hours: a === 'extend' ? ENGINEER_VM_EXTEND_HOURS : undefined,
        tenant: live.tenant ?? '',
      });
      toast.success(`${a} requested for ${live.vm_id}.`);
    } catch (error) {
      // Surface the backend refusal verbatim (403 gate, 409 ErrVMConflict) —
      // the API wrapper puts the response text on `.name`.
      toast.error(
        error instanceof Error && error.name
          ? error.name
          : 'Engineer VM action failed.'
      );
    }
  };

  return (
    <Sheet open={!!vm} onOpenChange={o => !o && onClose()}>
      <SheetContent className="flex w-[420px] max-w-full flex-col gap-4">
        {live && (
          <>
            <SheetHeader>
              <SheetTitle className="font-mono text-sm">
                {live.vm_id}
              </SheetTitle>
              <SheetDescription>Engineer VM workspace</SheetDescription>
            </SheetHeader>

            <StatusChip
              label={engineerVMPhaseLabel[phase]}
              variant={engineerVMPhaseVariant[phase]}
            />

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Detail label="Customer" value={customerLabel(live.tenant, me)} />
              <Detail label="Tier" value={live.tier} />
              <Detail label="Private IP" value={live.private_ip} />
              <Detail label="Instance" value={live.instance_id} />
              <Detail label="Expires" value={fmtEpoch(live.expiry_at)} />
            </dl>

            <div className="flex flex-col gap-2">
              {transitional ? (
                <div className="text-chariot-text-secondary flex items-center gap-2 text-sm">
                  <Loader2
                    className={cn(
                      'text-chariot-text-secondary size-4',
                      SPINNER_MOTION
                    )}
                  />
                  {engineerVMPhaseLabel[phase]}…
                </div>
              ) : phase === 'purged' ? (
                <p className="text-chariot-text-secondary text-sm">
                  This VM has been purged.
                </p>
              ) : phase === 'archived' ? (
                <Button onClick={() => run('revive')} disabled={busy}>
                  Revive
                </Button>
              ) : (
                <>
                  <Button onClick={openIDE} disabled={!isRunning || busy}>
                    Open IDE (code-server)
                  </Button>
                  {isRunning ? (
                    <Button
                      variant="outline"
                      onClick={() => run('pause')}
                      disabled={busy}
                    >
                      Pause
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => run('resume')}
                      disabled={busy}
                    >
                      Resume
                    </Button>
                  )}
                  {isRunning && (
                    <Button
                      variant="outline"
                      onClick={() => run('extend')}
                      disabled={busy}
                    >
                      Extend +7d
                    </Button>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" disabled={busy}>
                        Archive
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Archive this VM?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Archives the VM — the data volume is snapshotted and
                          the instance terminated; you can Revive it.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className={buttonVariants({ variant: 'destructive' })}
                          onClick={() => run('archive')}
                        >
                          Archive
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>

            <p className="text-chariot-text-secondary text-xs">
              SSH from a terminal: <code>guard vm ssh {live.vm_id}</code>
            </p>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Detail({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-chariot-text-secondary text-xs">{label}</dt>
      <dd className="truncate">{value || '—'}</dd>
    </div>
  );
}

function fmtEpoch(ts?: number): string {
  if (!ts) return '—';
  return new Date(ts * 1000).toLocaleString();
}
